import { useEffect, useRef } from "react";
import type { ApiClient } from "../lib/api";

// Calls `onFrame` for every SSE frame of `type` on `path` (e.g.
// "/v1/stream/blocks" / "block_seen"). No React state: the caller decides what
// to keep, so a busy stream doesn't re-render anything by itself.
//
// - Closed while the tab is hidden, reopened when it's visible again.
// - On error, reopened after 3 s as a FRESH connection: the native EventSource
//   would send Last-Event-ID and the server would replay up to ~10k frames
//   (full blocks/operations), which callers don't need — they backfill over
//   REST anyway.
export function useSseFrames<T>(
  client: ApiClient,
  path: string,
  type: string,
  onFrame: (frame: T) => void,
  enabled = true,
) {
  const cb = useRef(onFrame);
  cb.current = onFrame;

  useEffect(() => {
    if (!enabled) return;
    const url = client.sseUrl(path);
    if (!url) return;
    let es: EventSource | null = null;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const close = () => {
      clearTimeout(retry);
      es?.close();
      es = null;
    };
    const open = () => {
      close();
      if (document.visibilityState === "hidden") return;
      es = new EventSource(url);
      es.onmessage = (ev) => {
        try {
          const f = JSON.parse(ev.data) as { type?: string } & T;
          if (f?.type === type) cb.current(f);
        } catch {
          // ignore malformed payloads
        }
      };
      es.onerror = () => {
        close();
        retry = setTimeout(open, 3_000);
      };
    };
    const onVis = () =>
      document.visibilityState === "hidden" ? close() : open();

    open();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      close();
    };
  }, [client, path, type, enabled]);
}
